<template>
	<div class="rank-page">
		<!-- 顶部筛选区 -->
		<el-card class="box-card filter-card">
			<el-form ref="queryForm" :model="queryParams" label-width="80px" inline size="small">
				<el-form-item label="日期范围:">
					<el-date-picker v-model="dateRange" type="daterange" range-separator="-"
						start-placeholder="开始日期" end-placeholder="结束日期" format="yyyy-MM-dd"
						value-format="yyyy-MM-dd" @change="handleDateChange">
					</el-date-picker>
				</el-form-item>
				<el-form-item>
					<el-button type="primary" @click="handleQuery">查询</el-button>
					<el-button @click="resetQuery">重置</el-button>
					<el-button type="text" icon="el-icon-back" @click="goBack">返回首页</el-button>
				</el-form-item>
			</el-form>
		</el-card>

		<!-- 统计卡片 -->
		<div class="stats-row" v-loading="loading">
			<div class="stat-card stat-total">
				<div class="stat-icon"><i class="el-icon-office-building"></i></div>
				<div class="stat-body">
					<div class="stat-label">运营商总数</div>
					<div class="stat-value">{{ stats.totalDistributors }}</div>
				</div>
			</div>
			<div class="stat-card stat-orders">
				<div class="stat-icon"><i class="el-icon-document"></i></div>
				<div class="stat-body">
					<div class="stat-label">订单总数</div>
					<div class="stat-value">{{ stats.totalOrders }}</div>
				</div>
			</div>
			<div class="stat-card stat-max">
				<div class="stat-icon"><i class="el-icon-trophy"></i></div>
				<div class="stat-body">
					<div class="stat-label">最高单运营商订单数</div>
					<div class="stat-value">{{ stats.maxOrders }}</div>
				</div>
			</div>
			<div class="stat-card stat-avg">
				<div class="stat-icon"><i class="el-icon-data-line"></i></div>
				<div class="stat-body">
					<div class="stat-label">平均订单数</div>
					<div class="stat-value">{{ stats.avgOrders }}</div>
				</div>
			</div>
		</div>

		<!-- TOP 20 横向条形图 -->
		<el-card class="box-card chart-card">
			<div slot="header" class="card-header">
				<span class="card-title">运营商订单数 TOP 20</span>
				<span class="card-subtitle">按订单数降序展示前20名</span>
			</div>
			<div id="rankChart" ref="rankChart" class="rank-chart" v-loading="loading"></div>
			<div v-if="!loading && rankList.length === 0" class="empty-tip">暂无数据</div>
		</el-card>

		<!-- 全部运营商卡片网格（可滚动） -->
		<el-card class="box-card grid-card">
			<div slot="header" class="card-header">
				<span class="card-title">全部运营商排行</span>
				<span class="card-subtitle">共 {{ rankList.length }} 家，滚动查看</span>
			</div>
			<div class="grid-scroll" v-loading="loading">
				<div v-if="!loading && rankList.length === 0" class="empty-tip">暂无数据</div>
				<div v-for="(item, index) in rankList" :key="item.distributorId" class="grid-item">
					<div class="grid-rank" :class="getRankClass(index)">{{ index + 1 }}</div>
					<div class="grid-name" :title="item.distributorName || item.distributorId">{{ item.distributorName || item.distributorId || '-' }}</div>
					<div class="grid-bar-wrapper">
						<div class="grid-bar" :style="getBarStyle(item.orderCount)"></div>
					</div>
					<div class="grid-count">{{ item.orderCount }} 单</div>
				</div>
			</div>
		</el-card>
	</div>
</template>

<script>
import { getCurrentDistributorId } from '@/utils/distributor.js'
import dayjs from 'dayjs'

export default {
	data() {
		return {
			isAdmin: false,
			loading: false,
			dateRange: [],
			distributorOptions: [],
			distributorInfoList: [],
			rankList: [],
			rankChart: null,
			maxCount: 1,
			stats: {
				totalDistributors: 0,
				totalOrders: 0,
				maxOrders: 0,
				avgOrders: '0'
			},
			queryParams: {
				distributorId: '',
				startDate: '',
				endDate: '',
				queryDistributorId: ''
			},
			_resizeHandler: null
		}
	},
	created() {
		this.isAdmin = window.sessionStorage.getItem('isAdmin') === 'true';
		this.queryParams.queryDistributorId = getCurrentDistributorId();
		this.initDateRange();
		this.getDistributorOptions();
		this.getList();
		this.$root.$on('distributorChanged', this.handleDistributorChange);
	},
	mounted() {
		this.$nextTick(() => {
			this.initChart();
		});
		if (!this._resizeHandler) {
			this._resizeHandler = () => {
				if (this.rankChart) this.rankChart.resize();
			};
			window.addEventListener('resize', this._resizeHandler);
		}
	},
	beforeDestroy() {
		this.$root.$off('distributorChanged', this.handleDistributorChange);
		if (this._resizeHandler) {
			window.removeEventListener('resize', this._resizeHandler);
			this._resizeHandler = null;
		}
		if (this.rankChart) {
			this.rankChart.dispose();
			this.rankChart = null;
		}
	},
	methods: {
		handleDistributorChange(distributorId) {
			this.queryParams.queryDistributorId = distributorId || '';
			this.getList();
		},
		initDateRange() {
			const today = dayjs();
			this.dateRange = [today.format('YYYY-MM-DD'), today.format('YYYY-MM-DD')];
			this.queryParams.startDate = this.dateRange[0];
			this.queryParams.endDate = this.dateRange[1];
		},
		handleDateChange(val) {
			if (val) {
				this.queryParams.startDate = val[0];
				this.queryParams.endDate = val[1];
			} else {
				this.queryParams.startDate = '';
				this.queryParams.endDate = '';
			}
		},
		async getList() {
			this.loading = true;
			try {
				const startDate = this.queryParams.startDate ? this.queryParams.startDate + ' 00:00:00' : '';
				const endDate = this.queryParams.endDate ? this.queryParams.endDate + ' 23:59:59' : '';
				const { data: res } = await this.$http.get('/dashboard/distributor-order-rank-all', {
					params: {
						startTime: startDate,
						endTime: endDate,
						queryDistributorId: this.queryParams.queryDistributorId
					}
				});
				if (res.code != 200) {
					this.$message.error(res.msg);
					return;
				}
				this.rankList = res.data || [];
				this.computeStats();
				this.updateChart();
			} catch (e) {
				this.$message.error('获取数据失败');
			} finally {
				this.loading = false;
			}
		},
		computeStats() {
			const list = this.rankList;
			this.stats.totalDistributors = list.length;
			const total = list.reduce((sum, item) => sum + (item.orderCount || 0), 0);
			this.stats.totalOrders = total;
			this.stats.maxOrders = list.length > 0 ? (list[0].orderCount || 0) : 0;
			this.stats.avgOrders = list.length > 0 ? (total / list.length).toFixed(1) : '0';
			this.maxCount = this.stats.maxOrders > 0 ? this.stats.maxOrders : 1;
		},
		handleQuery() {
			this.getList();
		},
		resetQuery() {
			this.initDateRange();
			this.queryParams.distributorId = '';
			this.getList();
		},
		goBack() {
			this.$router.push('/welcome');
		},
		getDistributorOptions() {
			this.$http.get('/distributor/manager/getAllDistributorInfo').then((res) => {
				this.distributorOptions = [];
				this.distributorInfoList = [];
				if (res.data.code != 200) return this.$message.error(res.data.msg);
				let list = res.data.data;
				for (let index in list) {
					this.distributorOptions.push({ key: list[index].id, value: list[index].name });
				}
				this.distributorInfoList = list;
			});
		},
		getRankClass(index) {
			if (index === 0) return 'grid-rank-1';
			if (index === 1) return 'grid-rank-2';
			if (index === 2) return 'grid-rank-3';
			return '';
		},
		getBarStyle(orderCount) {
			const percent = this.maxCount > 0 ? (orderCount / this.maxCount) * 100 : 0;
			return { width: percent + '%' };
		},
		initChart() {
			const dom = this.$refs.rankChart;
			if (!dom) return;
			if (this.rankChart) this.rankChart.dispose();
			this.rankChart = this.$echarts.init(dom);
			this.updateChart();
		},
		updateChart() {
			if (!this.rankChart) {
				this.initChart();
				return;
			}
			// TOP 20，图表 y 轴从下往上展示，需倒序
			const top = this.rankList.slice(0, 20).reverse();
			const names = top.map(item => {
				const name = item.distributorName || item.distributorId || '-';
				return name.length > 12 ? name.substring(0, 12) + '...' : name;
			});
			const counts = top.map(item => item.orderCount || 0);

			this.rankChart.setOption({
				tooltip: {
					trigger: 'axis',
					axisPointer: { type: 'shadow' },
					formatter: (params) => {
						const idx = params[0].dataIndex;
						const item = top[idx];
						return `${item.distributorName || item.distributorId || '-'}<br/>订单数：<b>${item.orderCount}</b>`;
					}
				},
				grid: {
					left: '3%',
					right: '5%',
					bottom: '3%',
					containLabel: true
				},
				xAxis: {
					type: 'value',
					name: '订单数'
				},
				yAxis: {
					type: 'category',
					data: names,
					axisLabel: {
						fontSize: 12
					}
				},
				series: [{
					type: 'bar',
					data: counts,
					barMaxWidth: 22,
					itemStyle: {
						color: (params) => {
							// 前三名（倒序后是后三个）高亮
							const total = top.length;
							const rankFromTop = total - params.dataIndex;
							if (rankFromTop === 1) return '#f56c6c';
							if (rankFromTop === 2) return '#e6a23c';
							if (rankFromTop === 3) return '#67c23a';
							return '#409EFF';
						}
					},
					label: {
						show: true,
						position: 'right',
						formatter: '{c}'
					}
				}]
			}, true);
		}
	}
}
</script>

<style scoped>
.rank-page {
	padding: 16px;
	background-color: #f5f7fa;
	min-height: calc(100vh - 84px);
}

.box-card {
	box-shadow: 0 1px 1px rgba(0, 0, 0, 0.08);
	margin-bottom: 16px;
}

/* 统计卡片行 */
.stats-row {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 16px;
	margin-bottom: 16px;
}

.stat-card {
	display: flex;
	align-items: center;
	gap: 12px;
	background-color: #fff;
	border-radius: 8px;
	padding: 16px;
	box-shadow: 0 1px 1px rgba(0, 0, 0, 0.08);
}

.stat-icon {
	width: 44px;
	height: 44px;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.stat-icon i {
	font-size: 22px;
	color: #fff;
}

.stat-total .stat-icon { background-color: #409EFF; }
.stat-orders .stat-icon { background-color: #67C23A; }
.stat-max .stat-icon { background-color: #f56c6c; }
.stat-avg .stat-icon { background-color: #E6A23C; }

.stat-body {
	flex: 1;
	min-width: 0;
}

.stat-label {
	font-size: 12px;
	color: #909399;
	margin-bottom: 4px;
}

.stat-value {
	font-size: 22px;
	font-weight: bold;
	color: #303133;
}

/* 图表区 */
.chart-card {
	padding: 0;
}

.card-header {
	display: flex;
	align-items: baseline;
	gap: 12px;
}

.card-title {
	font-size: 15px;
	font-weight: 600;
	color: #303133;
}

.card-subtitle {
	font-size: 12px;
	color: #909399;
}

.rank-chart {
	width: 100%;
	height: 520px;
}

.empty-tip {
	text-align: center;
	color: #909399;
	padding: 40px 0;
}

/* 卡片网格 */
.grid-scroll {
	display: grid;
	grid-template-columns: repeat(5, 1fr);
	gap: 12px;
	max-height: 600px;
	overflow-y: auto;
	padding: 4px;
}

.grid-item {
	display: flex;
	flex-direction: column;
	gap: 6px;
	padding: 10px;
	background-color: #f5f7fa;
	border-radius: 8px;
	border-left: 3px solid #c0c4cc;
	transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.grid-item:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.grid-rank {
	width: 24px;
	height: 24px;
	line-height: 24px;
	text-align: center;
	border-radius: 50%;
	background-color: #c0c4cc;
	color: #fff;
	font-weight: bold;
	font-size: 12px;
}

.grid-rank-1 { background-color: #f56c6c; }
.grid-rank-2 { background-color: #e6a23c; }
.grid-rank-3 { background-color: #67c23a; }

.grid-name {
	color: #303133;
	font-size: 13px;
	font-weight: 500;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.grid-bar-wrapper {
	height: 6px;
	background-color: #fff;
	border-radius: 3px;
	overflow: hidden;
}

.grid-bar {
	height: 100%;
	background: linear-gradient(90deg, #409EFF, #67C23A);
	border-radius: 3px;
	transition: width 0.5s ease;
}

.grid-count {
	color: #606266;
	font-weight: 600;
	font-size: 13px;
	text-align: right;
}

@media (max-width: 1200px) {
	.stats-row { grid-template-columns: repeat(2, 1fr); }
	.grid-scroll { grid-template-columns: repeat(3, 1fr); }
}

@media (max-width: 768px) {
	.stats-row { grid-template-columns: 1fr; }
	.grid-scroll { grid-template-columns: repeat(2, 1fr); }
}
</style>
